const VaultSearch = (() => {
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/&/g, ' and ').replace(/[^\p{L}\p{N}]+/gu, ' ').trim().replace(/\s+/g, ' ');
  const identities = [["Abraham Lincoln",["Honest Abe","Abe Lincoln"],["US president","American history","Civil War"]],["John F. Kennedy",["JFK","John Fitzgerald Kennedy"],["US president","American history"]],["Franklin D. Roosevelt",["FDR","Franklin Delano Roosevelt"],["US president","New Deal","World War II"]],["Theodore Roosevelt",["Teddy Roosevelt","Teddy","TR"],["US president","American history"]],["Albert Einstein",["Einstein"],["scientist","physicist","relativity","science"]],["Nikola Tesla",["Tesla","Nicolas Tesla","Nikolai Tesla"],["scientist","inventor","electricity","engineering"]],["Isaac Newton",["Sir Isaac Newton"],["scientist","physicist","gravity","science"]],["Charles Darwin",["Darwin"],["scientist","naturalist","evolution","biology"]],["Marcus Aurelius",["Marcus","The Philosopher Emperor"],["stoic","stoicism","Roman emperor","philosopher","philosophy","Meditations"]],["Seneca",["Seneca the Younger"],["stoic","stoicism","Roman philosopher","philosophy"]],["Epictetus",[],["stoic","stoicism","Greek philosopher","philosophy"]],["Zeno of Citium",["Zeno"],["stoic","stoicism","Greek philosopher","philosophy"]],["Socrates",[],["Greek philosopher","philosophy","Ancient Greece"]],["Aristotle",[],["Greek philosopher","philosophy","Ancient Greece"]],["Plato",[],["Greek philosopher","philosophy","Ancient Greece"]],["Julius Caesar",["Caesar","Gaius Julius Caesar"],["Roman leader","Roman general","Ancient Rome"]],["Augustus",["Octavian","Caesar Augustus"],["Roman emperor","Ancient Rome"]],["William Shakespeare",["Shakespeare","The Bard"],["writer","playwright","English literature"]],["Mark Twain",["Samuel Clemens","Samuel Langhorne Clemens"],["writer","author","American literature"]],["Winston Churchill",["Churchill"],["British prime minister","World War II","British history"]],["Napoleon Bonaparte",["Napoleon"],["French emperor","military history"]],["Hades",["Pluto"],["Greek mythology","Greek god","underworld"]],["Athena",["Pallas Athena"],["Greek mythology","Greek goddess","wisdom"]],["Medusa",[],["Greek mythology","Gorgon"]],["Homer",[],["Greek poet","writer","Iliad","Odyssey"]]];
  const stop = new Set('a an the of for in on at to by with and or do you have any me show looking want i is are please bust busts statue statues sculpture sculptures'.split(' '));
  const singular = word => ({ stoics: 'stoic', scientists: 'scientist', philosophers: 'philosopher', presidents: 'president', emperors: 'emperor', gods: 'god', writers: 'writer', authors: 'author', inventors: 'inventor', artists: 'artist', leaders: 'leader', thinkers: 'thinker' }[word] || word);
  const tokens = value => [...new Set(normalize(value).split(' ').filter(Boolean).map(singular))];
  function distance(a, b) {
    if (Math.abs(a.length - b.length) > 2) return 3;
    const rows = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
    for (let i = 0; i <= a.length; i++) rows[i][0] = i;
    for (let j = 0; j <= b.length; j++) rows[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        rows[i][j] = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + 1);
      }
    }
    return rows[a.length][b.length];
  }
  function enrich(product) {
    const title = ' ' + normalize(product.title) + ' ';
    const identitiesForProduct = identities.filter(([name]) => title.includes(' ' + normalize(name) + ' ') && !title.includes(' or ') && !product.title.includes('(?)'));
    const aliases = [...(product.aliases || []), ...identitiesForProduct.flatMap(identity => identity[1])];
    const topics = [...(product.topics || []), ...identitiesForProduct.flatMap(identity => identity[2])];
    const tags = (product.tags || []).filter(tag => !/^(bv-order|vault-trio):/i.test(tag));
    const collections = product.collections || [];
    const subject = normalize([product.title, ...tags, ...topics, ...collections.map(shelf => shelf.title)].join(' '));
    const categories = ['all'];
    if (/us president|u s president/.test(subject)) categories.push('presidents');
    if (/philosoph|stoic/.test(subject)) categories.push('philosophy');
    if (/science|scientist|inventor|physic/.test(subject)) categories.push('science');
    if (/mytholog|greek god|sacred art/.test(subject)) categories.push('mythology');
    if (/arts entertainment|literature|writer|author|poet|playwright/.test(subject)) categories.push('arts');
    return { ...product, aliases, topics, tags, collections, categories };
  }
  function prepare(products) {
    return products.map(enrich).map(product => ({ ...product, fields: [
      { kind: 'name', label: product.title, weight: 12, text: normalize(product.title), words: tokens(product.title) },
      ...(product.aliases || []).map(alias => ({ kind: 'alias', label: alias, weight: 11, text: normalize(alias), words: tokens(alias) })),
      ...(product.topics || []).map(topic => ({ kind: 'topic', label: topic, weight: 8, text: normalize(topic), words: tokens(topic) })),
      ...(product.tags || []).map(tag => ({ kind: 'tag', label: tag, weight: 7, text: normalize(tag), words: tokens(tag) })),
      ...product.collections.flatMap(shelf => [
        { kind: 'collection', label: shelf.title, weight: 5, text: normalize(shelf.title), words: tokens(shelf.title) },
        { kind: 'description', label: shelf.title, weight: 2, text: normalize(shelf.description || ''), words: tokens(shelf.description || '') }
      ]),
      { kind: 'description', label: 'Product description', weight: 3, text: normalize(product.description), words: tokens(product.description) }
    ] }));
  }
  function search(index, rawQuery, { category = 'all', mode = 'smart' } = {}) {
    const boundedQuery = String(rawQuery || '').slice(0, 120);
    const query = normalize(boundedQuery);
    const queryWords = tokens(boundedQuery).filter(word => !stop.has(word)).slice(0, 8);
    const pool = category === 'all' ? index : index.filter(product => product.categories.includes(category));
    if (!query) return pool.map(product => ({ product, score: product.featured ? 10 : 0, reason: product.categories[0], fuzzy: false }));
    if (mode === 'exact') return pool.filter(product => normalize(product.title).includes(query)).map(product => ({ product, score: 1, reason: 'Title contains your search', fuzzy: false }));
    if (!queryWords.length) return pool.map(product => ({ product, score: product.featured ? 10 : 0, reason: product.categories[0], fuzzy: false }));
    const pairs = new Map();
    function similarity(word, target, field) {
      if (word === target) return { factor: 1, fuzzy: false };
      if (word.length >= 3 && target.startsWith(word)) return { factor: 0.85, fuzzy: false };
      if (field.kind === 'description' || word.length < 4 || target.length < 4) return null;
      const key = word + '|' + target;
      let edits = pairs.get(key);
      if (edits === undefined) { edits = distance(word, target); pairs.set(key, edits); }
      const maxEdits = word.length >= 7 && field.kind === 'name' && word.slice(0, 2) === target.slice(0, 2) ? 2 : 1;
      return edits <= maxEdits && edits / Math.max(word.length, target.length) <= 0.29 ? { factor: edits === 1 ? 0.72 : 0.53, fuzzy: true } : null;
    }
    const results = pool.flatMap(product => {
      const matches = queryWords.map(word => {
        let best = null;
        for (const field of product.fields) {
          for (const target of field.words) {
            const match = similarity(word, target, field);
            if (!match) continue;
            const score = field.weight * match.factor;
            if (!best || score > best.score) best = { ...match, score, field };
          }
        }
        return best;
      });
      if (matches.some(match => !match)) return [];
      let score = matches.reduce((sum, match) => sum + match.score, 0) / matches.length;
      const nameWords = product.fields[0].words;
      const sameName = queryWords.every(word => nameWords.includes(word));
      const allName = matches.every(match => match.field.kind === 'name');
      const alias = product.fields.find(field => field.kind === 'alias' && field.text === query);
      if (sameName) score += 30;
      else if (allName) score += 15;
      if (alias) score += 28;
      if (product.fields[0].text === query) score += 10;
      const strongest = matches.reduce((best, match) => match.score > best.score ? match : best);
      const fuzzy = matches.some(match => match.fuzzy);
      const reason = alias ? 'Also known as ' + alias.label : allName ? (fuzzy ? 'Close spelling match' : 'Name match') : strongest.field.kind === 'description' ? 'Found in the description' : strongest.field.label;
      return [{ product, score, reason, fuzzy, direct: sameName || Boolean(alias), meaningful: matches.some(match => match.field.kind !== 'description') }];
    });
    const hasDirect = results.some(result => result.direct);
    return results.filter(result => !hasDirect || !result.fuzzy && result.meaningful).sort((a, b) => b.score - a.score || a.product.title.length - b.product.title.length);
  }
  return { prepare, search, normalize, distance };
})();
if (typeof module !== 'undefined') module.exports = VaultSearch;

if (typeof document !== 'undefined') (() => {
  const root = document.querySelector('[data-vault-search]');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const find = selector => root.querySelector(selector);
  const input = find('[data-vault-query]');
  const form = find('[data-vault-form]');
  const results = find('[data-vault-results]');
  const count = find('[data-vault-count]');
  const reset = find('[data-vault-reset]');
  const moreRow = find('[data-vault-more-row]');
  const shelves = find('[data-vault-shelves]');
  const strings = JSON.parse(find('[data-vault-strings]').textContent);
  const categoryButtons = [...root.querySelectorAll('[data-vault-category]')];
  const allowedCategories = new Set(categoryButtons.map(button => button.dataset.vaultCategory));
  let category = 'all';
  let index;
  let matches = [];
  let shown = 24;
  let debounce;
  let ready = false;
  let searchState = null;
  let requestId = 0;
  let controller;
  let catalogRequest;
  const more = find('[data-vault-more]');
  const initialResults = readNativeResults(root);

  function restore() {
    const url = new URL(location.href);
    input.value = (url.searchParams.get('q') || '').slice(0, 120);
    category = allowedCategories.has(url.searchParams.get('interest')) ? url.searchParams.get('interest') : 'all';
  }

  function updateUrl() {
    const url = new URL(location.href);
    const query = input.value.trim();
    url.searchParams.set('type', 'product');
    if (query) url.searchParams.set('q', query);
    else url.searchParams.delete('q');
    if (category !== 'all') url.searchParams.set('interest', category);
    else url.searchParams.delete('interest');
    for (const key of [...url.searchParams.keys()]) {
      if (key === 'page' || key === 'sort_by' || key.startsWith('filter.')) url.searchParams.delete(key);
    }
    history.replaceState(null, '', url);
  }

  function safeUrl(value) {
    if (!value) return null;
    try {
      const url = new URL(value, location.origin);
      return url.protocol === 'https:' || url.origin === location.origin ? url.href : null;
    } catch { return null; }
  }

  function productKey(product) {
    return new URL(product.url, location.origin).pathname;
  }

  function uniqueProducts(products) {
    return [...new Map(products.map(product => [productKey(product), product])).values()];
  }

  function readNativeResults(scope) {
    const pagination = scope.querySelector('[data-vault-pagination]');
    if (!pagination) return null;
    const products = [...scope.querySelectorAll('.tbv-vault__card')].map(link => ({
      url: link.getAttribute('href'),
      title: link.querySelector('.tbv-vault__title').textContent.trim(),
      image: link.querySelector('img')?.getAttribute('src'),
      priceDisplay: link.querySelector('.tbv-vault__price').textContent.trim()
    }));
    return {
      query: pagination.dataset.query,
      page: Number(pagination.dataset.page),
      pages: Number(pagination.dataset.pages),
      total: Number(pagination.dataset.total),
      products
    };
  }

  async function fetchSection(url, signal) {
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
    const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.any([signal, AbortSignal.timeout(20000)]) });
    if (!response.ok) throw new Error('Search unavailable');
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  async function fetchNativeResults(query, page, signal) {
    if (initialResults?.query === query && initialResults.page === page) return initialResults;
    const url = new URL(form.action);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('page', page);
    url.searchParams.set('section_id', root.dataset.sectionId);
    const html = await fetchSection(url, signal);
    const data = readNativeResults(html);
    if (!data || data.page !== page || !Number.isInteger(data.total) || !Number.isInteger(data.pages)) throw new Error('Search unavailable');
    return data;
  }

  async function fetchHeaderResults(query, signal) {
    const url = new URL(root.dataset.predictiveUrl, location.origin);
    const header = document.querySelector('predictive-search-component[data-section-id]');
    url.searchParams.set('q', query);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', header?.dataset.sectionId || 'predictive-search');
    const html = await fetchSection(url, signal);
    if (!html.querySelector('#predictive-search-results')) throw new Error('Search unavailable');
    if (html.querySelector('.predictive-search-results__no-results')) return [];
    return [...html.querySelectorAll('#predictive-search-products .predictive-search-results__card--product')].map(item => ({
      url: item.querySelector('a[href]').getAttribute('href'),
      title: item.querySelector('.resource-card__title').textContent.trim(),
      image: item.querySelector('img')?.getAttribute('src'),
      priceDisplay: item.querySelector('.price')?.textContent.trim() || ''
    }));
  }

  function setCount(total) {
    count.textContent = (total === 1 ? strings.countOne : strings.count).replace('[count]', total.toLocaleString());
  }

  function card(product, position) {
    const link = document.createElement('a');
    link.className = 'tbv-vault__card';
    link.href = safeUrl(product.url) || '#';
    const stage = document.createElement('div');
    stage.className = 'tbv-vault__image';
    const imageUrl = safeUrl(product.image);
    if (imageUrl) {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.srcset = [180, 360, 540, 720].map(width => {
        const url = new URL(imageUrl);
        url.searchParams.set('width', width);
        return `${url.href} ${width}w`;
      }).join(', ');
      img.sizes = '(max-width: 749px) 48vw, 24vw';
      img.width = 720;
      img.height = 720;
      img.alt = product.title;
      img.loading = position < 4 ? 'eager' : 'lazy';
      img.decoding = 'async';
      if (position === 0) img.fetchPriority = 'high';
      stage.append(img);
    }
    const title = document.createElement('h2');
    title.className = 'tbv-vault__title';
    title.textContent = product.title;
    const price = document.createElement('p');
    price.className = 'tbv-vault__price';
    price.textContent = product.priceDisplay;
    link.append(stage, title, price);
    return link;
  }

  function renderShelves() {
    shelves.replaceChildren();
    shelves.hidden = true;
    if (!input.value.trim()) return;
    const query = VaultSearch.normalize(input.value);
    const unique = new Map(index.flatMap(product => product.collections).map(shelf => [shelf.handle, shelf]));
    const paths = [...unique.values()].filter(shelf => {
      const title = VaultSearch.normalize(shelf.title);
      return query.length >= 4 && (title.includes(query) || query.includes(title));
    }).slice(0, 2);
    for (const shelf of paths) {
      const link = document.createElement('a');
      link.href = safeUrl(shelf.url) || '#';
      link.textContent = shelf.title;
      shelves.append(link);
    }
    shelves.hidden = !paths.length;
  }

  function renderCards(append = false) {
    if (!append) {
      results.replaceChildren();
      const grid = document.createElement('div');
      grid.className = 'tbv-vault__grid';
      results.append(grid);
    }
    const grid = results.querySelector('.tbv-vault__grid');
    const start = append ? grid.children.length : 0;
    const fragment = document.createDocumentFragment();
    matches.slice(start, shown).forEach(({ product }, position) => fragment.append(card(product, start + position)));
    grid.append(fragment);
    if (!matches.length) {
      results.append(find('[data-vault-empty]').content.cloneNode(true));
      const all = find('[data-vault-search-all]');
      all.hidden = category === 'all';
      all.addEventListener('click', () => { category = 'all'; render(); });
    }
    moreRow.hidden = shown >= matches.length && (!searchState || searchState.page >= searchState.pages);
    if (append && grid.children[start]) {
      grid.children[start].tabIndex = -1;
      grid.children[start].focus({ preventScroll: true });
    }
  }

  async function render(writeUrl = true) {
    const query = input.value.trim();
    controller?.abort();
    controller = new AbortController();
    const signal = controller.signal;
    const currentRequest = ++requestId;
    searchState = null;
    shown = 24;
    more.disabled = false;
    if (query) category = 'all';
    reset.hidden = !query && category === 'all';
    find('[data-vault-categories]').hidden = Boolean(query) || !ready;
    find('[data-vault-notice]').hidden = true;
    categoryButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.vaultCategory === category)));
    if (writeUrl) updateUrl();
    if (!query) {
      if (!ready) {
        count.textContent = strings.loading;
        results.setAttribute('aria-busy', 'true');
        try {
          catalogRequest ||= initialize().catch(error => { catalogRequest = null; throw error; });
          await catalogRequest;
          if (currentRequest !== requestId) return;
        } catch {
          if (currentRequest !== requestId) return;
          if (writeUrl) { form.submit(); return; }
          delete root.dataset.loading;
          count.textContent = originalCount;
          results.removeAttribute('aria-busy');
          find('[data-vault-notice]').hidden = false;
          return;
        }
      }
      matches = VaultSearch.search(index, '', { category });
      setCount(matches.length);
      renderShelves();
      renderCards();
      find('[data-vault-categories]').hidden = false;
      results.removeAttribute('aria-busy');
      return;
    }
    count.textContent = strings.loading;
    results.setAttribute('aria-busy', 'true');
    moreRow.hidden = true;
    shelves.hidden = true;
    const page = writeUrl ? 1 : Math.max(1, Number(new URL(location.href).searchParams.get('page')) || 1);
    try {
      const [native, predictive] = await Promise.all([
        fetchNativeResults(query, page, signal),
        fetchHeaderResults(query, signal).catch(error => { if (signal.aborted) throw error; return null; })
      ]);
      if (currentRequest !== requestId) return;
      const noMatches = predictive !== null && predictive.length === 0;
      const products = noMatches ? [] : uniqueProducts([...(page === 1 ? predictive || [] : []), ...native.products]);
      searchState = { ...native, products, total: noMatches ? 0 : native.total, pages: noMatches ? 0 : native.pages };
      matches = products.map(product => ({ product }));
      setCount(Math.max(searchState.total, products.length));
      renderCards();
      find('[data-vault-notice]').hidden = predictive !== null;
    } catch {
      if (currentRequest !== requestId || signal.aborted) return;
      if (writeUrl) {
        form.submit();
        return;
      }
      count.textContent = originalCount;
      find('[data-vault-notice]').hidden = false;
    } finally {
      if (currentRequest === requestId) {
        delete root.dataset.loading;
        results.removeAttribute('aria-busy');
      }
    }
  }

  async function showMore() {
    const currentRequest = requestId;
    const state = searchState;
    const nextShown = shown + 24;
    more.disabled = true;
    try {
      while (state && state.products.length < nextShown && state.page < state.pages) {
        const next = await fetchNativeResults(state.query, state.page + 1, controller.signal);
        if (currentRequest !== requestId) return;
        state.products = uniqueProducts([...state.products, ...next.products]);
        state.page = next.page;
      }
      if (currentRequest !== requestId) return;
      if (state) matches = state.products.map(product => ({ product }));
      shown = nextShown;
      renderCards(true);
    } catch {
      if (currentRequest !== requestId) return;
      find('[data-vault-notice]').hidden = false;
    } finally {
      if (currentRequest === requestId) more.disabled = false;
    }
  }

  async function fetchPage(page) {
    const url = new URL(root.dataset.indexUrl, location.origin);
    url.searchParams.set('section_id', 'tbv-vault-index');
    url.searchParams.set('sort_by', 'best-selling');
    url.searchParams.set('page', page);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
    const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(20000) });
    if (!response.ok) throw new Error('Catalog unavailable');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const payload = html.querySelector('[data-vault-index]');
    if (!payload) throw new Error('Catalog unavailable');
    const data = JSON.parse(payload.textContent);
    if (!Array.isArray(data.products) || !Number.isInteger(data.pages) || data.page !== page || data.pages > 100) throw new Error('Incomplete catalog');
    return data;
  }

  async function initialize() {
    const first = await fetchPage(1);
    const pages = [first];
    for (let next = 2; next <= first.pages; next += 4) {
      const batch = Array.from({ length: Math.min(4, first.pages - next + 1) }, (_, offset) => fetchPage(next + offset));
      pages.push(...await Promise.all(batch));
    }
    const products = pages.flatMap(page => page.products);
    if (pages.some(page => page.total !== first.total) || products.length !== first.total || new Set(products.map(product => product.id)).size !== first.total) throw new Error('Incomplete catalog');
    index = VaultSearch.prepare(products);
    ready = true;
    delete root.dataset.loading;
    root.dataset.catalogSize = String(index.length);
    find('[data-vault-catalog-count]').textContent = strings.catalog.replace('[count]', index.length.toLocaleString());
  }

  restore();
  const originalCount = count.textContent;
  root.dataset.loading = 'true';
  count.textContent = strings.loading;
  form.addEventListener('submit', event => {
    if (!ready && !input.value.trim()) return;
    event.preventDefault();
    clearTimeout(debounce);
    render();
    input.blur();
  });
  input.addEventListener('input', () => {
    controller?.abort();
    requestId++;
    clearTimeout(debounce);
    debounce = setTimeout(() => render(), 250);
  });
  root.querySelectorAll('[data-vault-example]').forEach(link => link.addEventListener('click', event => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    input.value = link.dataset.vaultExample;
    category = 'all';
    render();
  }));
  categoryButtons.forEach(button => button.addEventListener('click', () => {
    category = button.dataset.vaultCategory;
    render();
  }));
  reset.addEventListener('click', () => { input.value = ''; category = 'all'; render(); input.focus(); });
  more.addEventListener('click', showMore);
  window.addEventListener('popstate', () => { restore(); render(false); });
  render(false);
})();
